import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaTachometerAlt,
  FaSearch,
  FaChartLine,
  FaAd,
  FaStar,
  FaGlobe,
  FaImages,
  FaChartPie,
  FaChevronDown,
  FaChevronUp,
  FaLightbulb,
  FaBalanceScale,
  FaTimes,
  FaHeartbeat
} from "react-icons/fa";
import logo from "../logo.svg";

const DashboardSidebar = ({ isOpen, onClose }) => {
  const [keywordResearchOpen, setKeywordResearchOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const location = useLocation();

  const toggleKeywordResearch = () =>
    setKeywordResearchOpen(!keywordResearchOpen);
  const toggleAnalytics = () => setAnalyticsOpen(!analyticsOpen);

  const isActive = (path) => location.pathname === path;

  return (
    <aside className="bg-gray-900 text-white h-full flex flex-col shadow-2xl border-r border-gray-800 relative w-80">
       {/* Close/Back Button (Only when open) */}
       {isOpen && (
        <div className="absolute top-4 right-4 flex gap-2">
             {/* <button 
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors focus:outline-none"
                title="Back"
            >
                <FaArrowLeft size={16} />
            </button> */}
             <button 
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors focus:outline-none"
                title="Close Sidebar"
            >
                <FaTimes size={20} />
            </button>
        </div>
      )}

      <div className="sidebar-header p-6 border-b border-gray-800 flex items-center justify-center">
        <div className="flex items-center space-x-2">
           <img src={logo} alt="AppOptimax Logo" className="h-8 w-8" />
           {/* <h2 className="text-2xl font-bold tracking-wider text-blue-500">AppOptimax</h2> */}
        </div>
      </div>
      
      <div className="sidebar-content flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent custom-scrollbar">
        <ul className="space-y-1">
          <li>
            <Link
              to="/dashboard/overview"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/overview")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaTachometerAlt className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">OVERVIEW</span>
            </Link>
          </li>

          {/* Keyword Research Menu */}
          <li>
            <button
              className={`w-full flex items-center justify-between px-6 py-3 transition-colors duration-200 ${
                keywordResearchOpen ? "bg-gray-800 text-white font-bold" : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
              onClick={toggleKeywordResearch}
            >
              <div className="flex items-center">
                <FaSearch className="mr-3 text-lg flex-shrink-0" />
                <span className="text-sm whitespace-nowrap">KEYWORD</span>
              </div>
              {keywordResearchOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            </button>
            
            {keywordResearchOpen && (
              <ul className="bg-gray-950 py-2">
                {[
                  { path: "/dashboard/keyword-research", label: "KEYWORD RESEARCH", icon: <FaSearch size={14} /> },
                  { path: "/dashboard/keyword-relevancy", label: "KEYWORD RELEVANCY", icon: <FaBalanceScale size={14} /> },
                  { path: "/dashboard/suggested-keywords", label: "SUGGESTED KEYWORDS", icon: <FaLightbulb size={14} /> },
                  { path: "/dashboard/keyword-difficulty", label: "KEYWORD DIFFICULTY", icon: <FaChartLine size={14} /> },
                  { path: "/dashboard/keyword-health", label: "KEYWORD HEALTH", icon: <FaHeartbeat size={14} /> },
                ].map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center pl-12 pr-6 py-3 text-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? "text-blue-400 font-bold bg-gray-800"
                          : "text-gray-300 hover:text-white hover:bg-gray-900 font-semibold"
                      }`}
                    >
                      <span className={`mr-3 flex-shrink-0 ${isActive(item.path) ? "opacity-100" : "opacity-70"}`}>{item.icon}</span>
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>

          <li>
            <Link
              to="/dashboard/competitor-analysis"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/competitor-analysis")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaChartLine className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">COMPETITOR ANALYSIS</span>
            </Link>
          </li>
          
          <li>
            <Link
              to="/dashboard/competitor-screenshots"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/competitor-screenshots")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaImages className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">COMPETITOR SCREENSHOTS</span>
            </Link>
          </li>

          <li>
            <Link
              to="/dashboard/ad-mob"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/ad-mob")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaAd className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">ADMOB</span>
            </Link>
          </li>

          <li>
            <Link
              to="/dashboard/review-rating-management"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/review-rating-management")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaStar className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">REVIEWS & RATINGS</span>
            </Link>
          </li>

          <li>
            <Link
              to="/dashboard/forecast-app"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/forecast-app")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaChartLine className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">APP FORECASTING</span>
            </Link>
          </li>

          <li>
            <Link
              to="/dashboard/localized-aso-insights"
              className={`flex items-center px-6 py-3 transition-colors duration-200 ${
                isActive("/dashboard/localized-aso-insights")
                  ? "bg-blue-600 text-white border-l-4 border-white font-bold"
                  : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
            >
              <FaGlobe className="mr-3 text-lg flex-shrink-0" />
              <span className="text-sm whitespace-nowrap">LOCALIZED ASO</span>
            </Link>
          </li>

          {/* Analytics & Reporting Menu */}
          <li>
            <button
              className={`w-full flex items-center justify-between px-6 py-3 transition-colors duration-200 ${
                analyticsOpen ? "bg-gray-800 text-white font-bold" : "text-gray-300 hover:bg-gray-800 hover:text-white font-semibold"
              }`}
              onClick={toggleAnalytics}
            >
              <div className="flex items-center">
                <FaChartPie className="mr-3 text-lg flex-shrink-0" />
                <span className="text-sm whitespace-nowrap">ANALYTICS</span>
              </div>
              {analyticsOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            </button>
            
            {analyticsOpen && (
              <ul className="bg-gray-950 py-2">
                {[
                  { path: "/dashboard/analytics-reporting", label: "ANALYTICS DATA" },
                  { path: "/dashboard/kpi-data", label: "KPI DATA" },
                ].map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center pl-12 pr-6 py-3 text-sm transition-all duration-200 ${
                        isActive(item.path)
                          ? "text-blue-400 font-bold bg-gray-800"
                          : "text-gray-300 hover:text-white hover:bg-gray-900 font-semibold"
                      }`}
                    >
                      <span className={`mr-3 flex-shrink-0 ${isActive(item.path) ? "opacity-100" : "opacity-70"}`}><FaChartPie size={14}/></span>
                      <span className="whitespace-nowrap">{item.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </li>
        </ul>
      </div>
      
      <div className="p-4 border-t border-gray-800 text-center text-xs text-gray-400">
        &copy; 2026 AppOptimax
      </div>
    </aside>
  );
};

export default DashboardSidebar;


